import {
  Box,
  Card,
  CircularProgress,
  Grid,
  Icon,
  IconButton,
  InputLabel,
} from "@mui/material";
import MDInput from "components/MDInput";
import { MDTextFieldCalendar } from "components/MDTextFiedCalendar";
import MDBox from "components/MDBox";
import MDButton from "components/MDButton";
import MDSelect from "components/MDSelect";
import MDTypography from "components/MDTypography";
import { useMaterialUIController } from "context";
import { Form, Formik, useFormikContext } from "formik";
import PropTypes from "prop-types";
import { useEffect, useState } from "react";
import { errorAlert, successAlert } from "utils/Functions";
import { get, postNoResponse } from "utils/WebService";
import * as Yup from "yup";
import { useUser } from "context/UserContext";

export default function DeadlinesCreateEdit({
  id,
  closeCreateEdit,
  refreshTable,
  casoProcessualId,
}) {
  const [controller] = useMaterialUIController();
  const { darkMode } = controller;
  const [isLoadingUsuarios, setIsLoadingUsuarios] = useState(false);
  const [usuarios, setUsuarios] = useState([]);
  const [isLoadingInfo, setIsLoadingInfo] = useState(false);

  const {
    statusPrazoEnum,
    tipoEventoEnum,
    userId
  } = useUser();

  useEffect(() => {
    setIsLoadingUsuarios(true);
    get(`/equipe/usuarios/${userId}`)
      .then(({ list }) => setUsuarios(list || []))
      .catch(() => setUsuarios([]))
      .finally(() => setIsLoadingUsuarios(false));
  }, []);

  const initialValues = {
    id: null,
    data: "",
    tipoPrazo: null,
    statusPrazo: statusPrazoEnum?.find(s => s.code === "pending") || null,
    pessoa: null,
    qtdDiasAvisar: null,
    dataPublicacao: "",
    descricao: "",
  };

  const validationSchema = Yup.object({
    data: Yup.string().required("Data é obrigatória"),
    dataPublicacao: Yup.string().required("data da Publicação é obrigatória"),
    tipoPrazo: Yup.object().nullable().required("Tipo de prazo é obrigatório"),
    statusPrazo: Yup.object().nullable().required("Status é obrigatório"),
    pessoa: Yup.object().nullable().required("Pessoa é obrigatória"),
    qtdDiasAvisar: Yup.number()
      .typeError("Deve ser um número")
      .min(0, "Não pode ser negativo")
      .required("Informe a quantidade de dias de aviso"),
  });

  const handleSubmit = (values, actions) => {
    const body = {
      id: values.id,
      data: values.data,
      tipoPrazo: values.tipoPrazo?.code,
      dataPublicacao: values.dataPublicacao,
      statusPrazo: values.statusPrazo?.code,
      pessoa: {
        id: values.pessoa?.id,
      },
      qtdDiasAvisar: Number(values.qtdDiasAvisar),
      descricao: values.descricao,
      processo: {
        id: casoProcessualId
      }
    };
    postNoResponse("/prazo/save", body)
      .then(() => {
        successAlert(
          !id ? "Prazo criado com sucesso!" : "Alterações salvas!",
          handleSuccess
        );
      })
      .catch((err) => {
        const padrao = !id ? "Erro ao criar prazo!" : "Erro ao salvar alterações.";
        // A mensagem do backend explica o motivo (ex.: limite de reagendamentos do tipo de evento)
        errorAlert(err?.message ? `${padrao} ${err.message}` : padrao);
      })
      .finally(() => actions.setSubmitting(false));
  };

  const handleSuccess = () => {
    closeCreateEdit();
    refreshTable();
  };

  return (
    <Card id="deadline-form">
      <Box display="flex" alignItems="center" p={3}>
        <IconButton onClick={closeCreateEdit} sx={{ p: 0, pr: 1, pt: 0.75 }}>
          <MDTypography variant="h5">
            <Icon>arrow_back</Icon>
          </MDTypography>
        </IconButton>
        <MDTypography variant="h6">
          {id ? "Editar" : "Adicionar"} Prazo
        </MDTypography>
      </Box>

      <Formik
        initialValues={initialValues}
        validationSchema={validationSchema}
        onSubmit={(values, actions) => {
          actions.setSubmitting(true);
          handleSubmit(values, actions);
        }}
      >
        {({ values, setFieldValue, isSubmitting, errors, touched }) => (
          <Form autoComplete="off">
            <FetchAndSetData
              id={id}
              setIsLoadingInfo={setIsLoadingInfo}
              closeCreateEdit={closeCreateEdit}
              usuarios={usuarios}
              statusPrazoEnum={statusPrazoEnum}
              tipoEventoEnum={tipoEventoEnum}
            />

            <MDBox px={3}>
              <Grid container spacing={2} pt={2}>
                <Grid item xs={12} sm={3}>
                  <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data Final do Prazo</InputLabel>
                  <MDTextFieldCalendar
                    name="data"
                    type="datetime-local"
                    fullWidth
                    value={values.data}
                    onChange={(e) => setFieldValue("data", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    darkMode={darkMode}
                  />
                  {errors.data && touched.data && (
                    <MDTypography variant="caption" color="error">
                      {errors.data}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12} sm={3}>
                  <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Data da Publicação</InputLabel>
                  <MDTextFieldCalendar
                    name="data"
                    type="date"
                    fullWidth
                    value={values.dataPublicacao}
                    onChange={(e) => setFieldValue("dataPublicacao", e.target.value)}
                    InputLabelProps={{ shrink: true }}
                    darkMode={darkMode}
                  />
                  {errors.dataPublicacao && touched.dataPublicacao && (
                    <MDTypography variant="caption" color="error">
                      {errors.dataPublicacao}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12} sm={3}>
                  <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>
                    Tipo de Prazo
                  </InputLabel>
                  <MDSelect
                    name="tipoPrazo"
                    placeholder="Selecione o tipo"
                    getOptionValue={option => option.code}
                    getOptionLabel={option => option.name}
                    value={values.tipoPrazo || []}
                    options={tipoEventoEnum}
                    onChange={(option) => setFieldValue("tipoPrazo", option)}
                  />
                  {errors.tipoPrazo && touched.tipoPrazo && (
                    <MDTypography variant="caption" color="error">
                      {errors.tipoPrazo}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12} sm={3}>
                  <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Status</InputLabel>
                  <MDSelect
                    name="statusPrazo"
                    placeholder="Selecione o status"
                    getOptionValue={option => option.code}
                    getOptionLabel={option => option.name}
                    value={values.statusPrazo || []}
                    options={statusPrazoEnum}
                    onChange={(option) => setFieldValue("statusPrazo", option)}
                  />
                  {errors.statusPrazo && touched.statusPrazo && (
                    <MDTypography variant="caption" color="error">
                      {errors.statusPrazo}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12} sm={3}>
                  <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Pessoa</InputLabel>
                  <MDSelect
                    name="pessoa"
                    isLoading={isLoadingUsuarios}
                    getOptionValue={(option) => option.id}
                    getOptionLabel={(option) => option.nome}
                    placeholder="Selecione a pessoa"
                    value={values.pessoa || ''}
                    options={usuarios}
                    onChange={(selected) => setFieldValue("pessoa", selected)}
                  />
                  {errors.pessoa && touched.pessoa && (
                    <MDTypography variant="caption" color="error">
                      {errors.pessoa}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12} sm={3}>
                  <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>
                    Qtd. dias antes para avisar
                  </InputLabel>
                  <MDInput
                    size="small"
                    type="number"
                    name="qtdDiasAvisar"
                    fullWidth
                    value={values.qtdDiasAvisar || ''}
                    onChange={(e) =>
                      setFieldValue("qtdDiasAvisar", e.target.value)
                    }
                  />
                  {errors.qtdDiasAvisar && touched.qtdDiasAvisar && (
                    <MDTypography variant="caption" color="error">
                      {errors.qtdDiasAvisar}
                    </MDTypography>
                  )}
                </Grid>

                <Grid item xs={12} sm={6}>
                  <InputLabel sx={{ px: 1, pb: 0.5, pt: 0.2 }}>Descrição</InputLabel>
                  <MDInput
                    multiline
                    name="descricao"
                    minRows={3}
                    fullWidth
                    placeholder="Descreva o prazo..."
                    value={values.descricao}
                    onChange={(e) =>
                      setFieldValue("descricao", e.target.value)
                    }
                  />
                </Grid>

                {/* Botão */}
                <Grid item xs={12}>
                  <Box
                    display="flex"
                    alignItems="center"
                    justifyContent="end"
                    p={2}
                  >
                    <MDButton
                      disabled={isLoadingInfo}
                      size="large"
                      variant="contained"
                      color="info"
                      circular={false}
                      iconOnly={false}
                      type="submit"
                      endIcon={
                        isSubmitting ? (
                          <CircularProgress
                            color="inherit"
                            size={20}
                            sx={{ ml: 0, mr: 0 }}
                          />
                        ) : null
                      }
                    >
                      {!isSubmitting && (id ? "Salvar" : "Adicionar")}
                    </MDButton>
                  </Box>
                </Grid>
              </Grid>
            </MDBox>
          </Form>
        )}
      </Formik>
    </Card>
  );
}

// Carrega o prazo só quando muda o id; o responsável entra quando a lista de usuários chegar. Antes o prazo era
// recarregado também quando a lista chegava, apagando o que o usuário já tinha alterado.
const FetchAndSetData = ({ id, setIsLoadingInfo, closeCreateEdit, usuarios, tipoEventoEnum, statusPrazoEnum }) => {
  const { setFieldValue, values } = useFormikContext();
  const [idPessoa, setIdPessoa] = useState(null);

  useEffect(() => {
    if (idPessoa && !values.pessoa) {
      const pessoa = usuarios.find((u) => u.id === idPessoa);
      if (pessoa) setFieldValue("pessoa", pessoa);
    }
  }, [idPessoa, usuarios]);

  useEffect(() => {
    if (id) {
      setIsLoadingInfo(true);
      get(`/prazo/by-id/${id}`)
        .then((response) => {
          const {
            id,
            data,
            tipoPrazo,
            statusPrazo,
            qtdDiasAvisar,
            descricao,
            dataPublicacao
          } = response;
          setFieldValue("id", id);
          setFieldValue("data", data || "");
          setFieldValue("dataPublicacao", dataPublicacao || "");
          setFieldValue(
            "tipoPrazo",
            tipoEventoEnum?.find(ht => ht.code === tipoPrazo)
          );
          setFieldValue(
            "statusPrazo",
            statusPrazoEnum?.find(ht => ht.code === statusPrazo)
          );
          setFieldValue(
            "pessoa",
            usuarios.find((u) => u.id === response.pessoa?.id) || null
          );
          setIdPessoa(response.pessoa?.id || null);
          setFieldValue("qtdDiasAvisar", qtdDiasAvisar || "");
          setFieldValue("descricao", descricao || "");
        })
        .catch(() => {
          errorAlert("Prazo não encontrado!", closeCreateEdit);
        })
        .finally(() => {
          setIsLoadingInfo(false);
        });
    }
  }, [id]);

  return null;
};

DeadlinesCreateEdit.propTypes = {
  id: PropTypes.string,
  closeCreateEdit: PropTypes.func.isRequired,
  refreshTable: PropTypes.func,
  casoProcessualId: PropTypes.string,
};
